import type { ConfigurationOperation } from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryBranchesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryBranchesService'
import {
  assertNotEmpty,
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useSuspenseQuery } from '@tanstack/react-query'

import * as configurationKeys from '../keys/configuration'

type FetchProps<O extends ConfigurationOperation> = {
  operationIds: Optional<string[]>
  server: ConfigurableServer
  validate: (operation: ConfigurationOperation) => operation is O
}

export const largeOperationsFetcher = async <T extends ConfigurationOperation>({
  operationIds,
  server,
  validate,
}: FetchProps<T>) => {
  assertNotEmpty(operationIds)

  const allOperations = operationIds.map((operationId) =>
    ConfigurationRepositoryBranchesService.branchServiceGetGetLargeOperationContent(
      {
        componentName: server.toLocaleLowerCase(),
        largeOperationId: operationId,
      },
    ),
  )

  const fetchedOperations = (await Promise.all(allOperations)).filter(notEmpty)

  if (fetchedOperations.length !== operationIds.length) {
    throw new Error('Could not fetch all of the requested operations')
  }

  return fetchedOperations.map((operation) => {
    if (!validate(operation)) {
      throw new Error('Response operation does not match expected type')
    }

    return operation
  })
}

export const useFetchLargeOperations = <T extends ConfigurationOperation>({
  operationIds,
  server,
  enabled,
  validate,
}: FetchProps<T> & { enabled: boolean }) => {
  const isEnabled = !!enabled && !!operationIds?.length

  return useSuspenseQuery({
    queryKey: isEnabled
      ? configurationKeys.fetchLargeOperationsKey({
          operationIds: operationIds ?? [],
          componentName: server,
        })
      : [],
    queryFn: () => {
      if (!isEnabled) {
        return Promise.resolve(null)
      }

      return largeOperationsFetcher({ operationIds, server, validate })
    },
  })
}
